import { useEffect, useRef } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker'

import { useIsSuperAdmin, useUserPermissions } from 'app/lib/hoc/withProtectedComponent'
import { getUserId, useTokenData } from 'app/lib/hooks/use-token-data'
import { getSnowplowSchema } from 'app/lib/utils/helpers'
import { ClientReportViewedEventData } from 'app/models/Organization'

export type ReportType = ClientReportViewedEventData['report_type']

interface UseTrackReportViewParams {
  isReportReady: boolean
  reportType: ReportType
  organizationIds: string[]
  reportPlatform: 'evidence'
}

export const useTrackReportView = ({
  isReportReady,
  reportType,
  organizationIds,
  reportPlatform,
}: UseTrackReportViewParams) => {
  const { user } = useAuth0()
  const userPermissions = useUserPermissions()
  const isSuperAdmin = useIsSuperAdmin()
  const token = useTokenData()
  const hasTrackedReportView = useRef(false)

  const canTrackReportView = isReportReady && !!userPermissions && !!user

  useEffect(() => {
    if (canTrackReportView && !hasTrackedReportView.current) {
      hasTrackedReportView.current = true

      const eventData: ClientReportViewedEventData = {
        report_type: reportType,
        user_type: isSuperAdmin ? 'super_admin' : 'organization_admin',
        user_id: getUserId(token),
        user_email: user?.email,
        organization_ids: organizationIds,
        report_platform: reportPlatform,
      }

      trackSelfDescribingEvent({
        event: {
          schema: getSnowplowSchema('client_report_viewed', '1-0-0'),
          data: eventData,
        },
      })
    }
  }, [canTrackReportView, reportType, organizationIds, userPermissions, token, user])
}
